import { useCards } from "@/pages/Dashboard";
import { useMemo, useState } from "react";
import { AlertTriangle, ChevronDown } from "lucide-react";

const statusColors = {
  "In Progress": "border-l-blue-500",
  "Completed": "border-l-emerald-500",
  "Blocked": "border-l-red-500"
};

const stageBadge = {
  "POC": "bg-blue-100 text-blue-700",
  "Pilot": "bg-amber-100 text-amber-700",
  "Production": "bg-emerald-100 text-emerald-700",
  "Support": "bg-purple-100 text-purple-700"
};

const TEAM_COMPONENTS = {
  "Hardware": ["Hardware"],
  "Firmware": ["Firmware"],
  "Software": ["Software"],
  "AI": ["AI/LLM"],
  "Production": ["Production"],
  "Delivery": ["Delivery & Support"],
  "Sales": ["Sales"],
  "Design": ["Hardware", "Software"]
};

function getBaseComponent(layerKey) {
  const colonIdx = layerKey.indexOf(": ");
  const group = colonIdx > -1 ? layerKey.substring(0, colonIdx) : layerKey;
  const bracketIdx = group.indexOf(" [");
  return bracketIdx > -1 ? group.substring(0, bracketIdx) : group;
}

function TeamCardItem({ card, team }) {
  const { openCardWithComponent } = useCards();
  const layerStatuses = card.layer_statuses || {};
  const relevantComponents = TEAM_COMPONENTS[team] || [];
  const hwConfigs = card.hardware_configs || [];

  const relevantLayers = Object.entries(layerStatuses).filter(([layer]) => {
    const base = getBaseComponent(layer);
    return relevantComponents.includes(base);
  });

  const done = relevantLayers.filter(([, v]) => v.status === "Done").length;
  const blocked = relevantLayers.filter(([, v]) => v.status === "Blocked").length;
  const total = relevantLayers.length;
  const progress = total > 0 ? Math.round((done / total) * 100) : 0;
  const primaryComponent = relevantComponents[0];

  return (
    <div
      onClick={() => openCardWithComponent(card, primaryComponent)}
      className={`bg-white border border-l-4 rounded p-2.5 cursor-pointer hover:shadow-sm ${statusColors[card.overall_status]}`}
      data-testid={`team-card-${card.id}`}
    >
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-1">
          <span className="font-mono text-xs font-bold text-slate-700">{card.sku}</span>
          {hwConfigs.length > 0 && relevantComponents.some(c => ["Hardware", "Firmware"].includes(c)) && (
            <span className="text-[9px] px-1 py-0.5 rounded bg-purple-100 text-purple-700">Multi</span>
          )}
        </div>
        {blocked > 0 && <AlertTriangle className="w-3 h-3 text-red-500" />}
      </div>
      <p className="text-sm text-slate-900 mb-1 line-clamp-1">{card.card_title}</p>
      <p className="text-xs text-slate-500 mb-2">{card.project_client}</p>

      {total > 0 && (
        <div className="flex items-center gap-2 mb-1">
          <div className="flex-1 h-1 bg-slate-200 rounded overflow-hidden">
            <div className={`h-full ${blocked > 0 ? 'bg-red-500' : 'bg-emerald-500'}`} style={{ width: `${progress}%` }}></div>
          </div>
          <span className="text-[10px] text-slate-500">{done}/{total}</span>
        </div>
      )}

      <div className="space-y-0.5 mt-2 pt-2 border-t border-slate-100">
        {relevantLayers.slice(0, 3).map(([layer, data]) => {
          const layerName = layer.includes(": ") ? layer.split(": ").slice(1).join(": ") : layer;
          const configMatch = layer.match(/\[(.+?)\]/);
          return (
            <div key={layer} className="flex items-center justify-between text-[10px]">
              <span className="text-slate-600 truncate flex-1">
                {configMatch ? `[${configMatch[1]}] ` : ''}{layerName}
              </span>
              <span className={`px-1 py-0.5 rounded ${
                data.status === "Done" ? "bg-emerald-100 text-emerald-700" :
                data.status === "Blocked" ? "bg-red-100 text-red-700" :
                data.status === "Testing" ? "bg-amber-100 text-amber-700" :
                data.status === "In Progress" ? "bg-blue-100 text-blue-700" :
                "bg-slate-100 text-slate-600"
              }`}>{data.status}</span>
            </div>
          );
        })}
        {relevantLayers.length > 3 && (
          <p className="text-[10px] text-slate-400">+{relevantLayers.length - 3} more</p>
        )}
      </div>

      <div className="flex items-center justify-between mt-2">
        <span className={`text-[10px] px-1 py-0.5 rounded ${stageBadge[card.lifecycle_stage]}`}>{card.lifecycle_stage}</span>
      </div>
    </div>
  );
}

function ArchiveSection({ cards, team }) {
  const [expanded, setExpanded] = useState(false);
  if (cards.length === 0) return null;

  return (
    <div className="mt-2 pt-2 border-t border-slate-200">
      <button onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700"
        data-testid={`archive-toggle-${team.toLowerCase()}`}
      >
        <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        Archived ({cards.length})
      </button>
      {expanded && (
        <div className="mt-2 space-y-2 opacity-60">
          {cards.map(card => <TeamCardItem key={card.id} card={card} team={team} />)}
        </div>
      )}
    </div>
  );
}

export default function TeamBoard({ activeCards, archivedCards }) {
  const { config } = useCards();
  const TEAMS = config?.teams || [];

  const groupedActive = useMemo(() => {
    const g = {};
    TEAMS.forEach(t => { g[t] = []; });
    activeCards.forEach(c => { if (g[c.team_owner]) g[c.team_owner].push(c); });
    return g;
  }, [activeCards, TEAMS]);

  const groupedArchived = useMemo(() => {
    const g = {};
    TEAMS.forEach(t => { g[t] = []; });
    archivedCards.forEach(c => { if (g[c.team_owner]) g[c.team_owner].push(c); });
    return g;
  }, [archivedCards, TEAMS]);

  const blockedTotal = activeCards.filter(c => c.blocker_count > 0).length;

  return (
    <div data-testid="team-board">
      {blockedTotal > 0 && (
        <div className="mb-4 p-2 bg-red-50 border border-red-200 rounded flex items-center gap-2 text-sm">
          <AlertTriangle className="w-4 h-4 text-red-600" />
          <span className="text-red-700">{blockedTotal} cards with blockers</span>
        </div>
      )}
      <div className="flex gap-3 overflow-x-auto pb-4">
        {TEAMS.map(team => {
          const teamCards = groupedActive[team] || [];
          const teamArchived = groupedArchived[team] || [];
          const blocked = teamCards.filter(c => c.blocker_count > 0).length;
          const relevantComponent = TEAM_COMPONENTS[team]?.[0] || team;

          return (
            <div key={team} className="w-64 flex-shrink-0 bg-slate-50 rounded" data-testid={`column-${team.toLowerCase()}`}>
              <div className="p-3 border-b border-slate-200 bg-white rounded-t">
                <div className="flex items-center justify-between">
                  <div>
                    <span className="font-semibold text-sm text-slate-900">{team}</span>
                    <p className="text-[10px] text-slate-400">{relevantComponent} focus</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {blocked > 0 && <span className="text-[10px] bg-red-500 text-white px-1 rounded">{blocked}</span>}
                    <span className="text-xs text-slate-500">{teamCards.length}</span>
                  </div>
                </div>
              </div>
              <div className="p-2 space-y-2 max-h-[500px] overflow-y-auto">
                {teamCards.length === 0 ? (
                  <p className="text-xs text-slate-400 text-center py-4">No cards</p>
                ) : (
                  teamCards.map(card => <TeamCardItem key={card.id} card={card} team={team} />)
                )}
                <ArchiveSection cards={teamArchived} team={team} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
